import React from 'react';
import _ from 'lodash';
import { Variable } from 'src/api/types';
import { Poppins } from 'src/common';
import { useStateSelector } from 'src/redux';
import { formatNumber } from 'src/utils/misc';
import styled from 'styled-components';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';
import { getVariableIcon } from 'src/components/FormulaInput/comps/variableIcon';

const Capsule = styled.div`
  background-color: ${({ theme }) => theme.colors.aliceBlue};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  padding: 2px 10px;
  display: inline-flex;
  align-items: center;
  grid-gap: 6px;

  .vc-icon {
    display: inline-flex;
    align-items: center;
  }
`;

// Amber "no value" warning — mirrors the edit-form VariableChip's no-data marker.
const WarnIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    style={{ transform: 'scale(0.8)', display: 'inline-flex' }}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    data-cy="no-data"
  >
    <path
      d="M9.7963 6.48145V9.99997M9.7963 13.5185H9.80509M18.5926 9.99997C18.5926 14.858 14.6544 18.7963 9.7963 18.7963C4.93824 18.7963 1 14.858 1 9.99997C1 5.14191 4.93824 1.20367 9.7963 1.20367C14.6544 1.20367 18.5926 5.14191 18.5926 9.99997Z"
      stroke={color}
      strokeWidth="1.75926"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// HTML form of the warning for the react-tooltip body (matches VariableChip).
const noDataHtml = (color: string) =>
  `<div style='display:flex;grid-gap:6px;align-items:center;'><span>No Data</span><svg style='transform:scale(0.8)' xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M9.7963 6.48145V9.99997M9.7963 13.5185H9.80509M18.5926 9.99997C18.5926 14.858 14.6544 18.7963 9.7963 18.7963C4.93824 18.7963 1 14.858 1 9.99997C1 5.14191 4.93824 1.20367 9.7963 1.20367C14.6544 1.20367 18.5926 5.14191 18.5926 9.99997Z" stroke="${color}" stroke-width="1.75926" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`;

interface VariableCapsuleProps {
  id: string;
  variables: Variable[];
  /** Names of workspace/global variables — drives override-icon detection. */
  workspaceVariableNames: Set<string>;
  /** When set (+ tooltipId), reveal the resolved value on hover. */
  showValueOnHover?: boolean;
  tooltipId?: string;
}

// Read-only formula variable chip. Resolution (incl. assessment→workspace
// override) and icon selection are reused VERBATIM from the edit form
// (findVariableByToken + getVariableIcon) so view/library/PDF match editing.
export const VariableCapsule: React.FC<VariableCapsuleProps> = ({
  id,
  variables,
  workspaceVariableNames,
  showValueOnHover,
  tooltipId,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const variable = findVariableByToken(variables, id);

  // Unresolved token (deleted/absent variable): keep the formula legible with a
  // raw-token + warning chip rather than silently dropping it.
  if (!variable) {
    return (
      <Capsule>
        <Poppins px={14} weight={500}>
          #{id}
        </Poppins>
        <WarnIcon color={colors.statusAmber} />
      </Capsule>
    );
  }

  const label = variable.alias || variable.name || `#${variable.id}`;
  const hasValue = !_.isNil(variable.value);
  const hoverProps =
    showValueOnHover && tooltipId
      ? {
          'data-tooltip-id': tooltipId,
          'data-tooltip-html': hasValue
            ? formatNumber(variable.value || 0, { symbol: !!variable.currency, maxDecimals: 3 })
            : noDataHtml(colors.statusAmber),
        }
      : {};

  return (
    <Capsule {...hoverProps}>
      <span className="vc-icon">{getVariableIcon(variable, workspaceVariableNames)}</span>
      <Poppins px={14} weight={500}>
        {label}
      </Poppins>
      {!hasValue && <WarnIcon color={colors.statusAmber} />}
    </Capsule>
  );
};
